"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShowroomCamera = void 0;
const core_1 = require("@babylonjs/core");
var ShowroomCameraState;
(function (ShowroomCameraState) {
    ShowroomCameraState[ShowroomCameraState["Matchmove"] = 0] = "Matchmove";
    ShowroomCameraState[ShowroomCameraState["ArcRotate"] = 1] = "ArcRotate";
})(ShowroomCameraState || (ShowroomCameraState = {}));
class ShowroomCamera {
    constructor(scene) {
        this._scene = scene;
        this._transform = new core_1.TransformNode("showroomRoot", this._scene);
        this._transform.rotationQuaternion = core_1.Quaternion.Identity();
        this._camera = new core_1.FreeCamera("showroomCamera", core_1.Vector3.Zero(), this._scene, true);
        this._camera.parent = this._transform;
        this._arcRotateCamera = new core_1.ArcRotateCamera("showroomArcRotateCamera", 0, 0, 0, core_1.Vector3.Zero(), this._scene, false);
        this._enableMouseWheel = true;
        this._perFrameObservable = new core_1.Observable();
        this._scene.onBeforeRenderObservable.add(() => {
            this._perFrameObservable.notifyObservers();
        });
        this._currentFocusPosition = core_1.Vector3.Zero();
        this._currentState = ShowroomCameraState.Matchmove;
    }
    set fov(value) {
        this._camera.fov = value;
        this._arcRotateCamera.fov = value;
    }
    set minZ(value) {
        this._camera.minZ = value;
        this._arcRotateCamera.minZ = value;
    }
    set maxZ(value) {
        this._camera.maxZ = value;
        this._arcRotateCamera.maxZ = value;
    }
    set enableMouseWheel(value) {
        this._enableMouseWheel = value;
        if (this._arcRotateCamera.inputs) {
            this._arcRotateCamera.inputs.remove(this._arcRotateCamera.inputs.attached.mousewheel);
        }
    }
    set arcRotateZoomPercent(value) {
        if (this._currentState === ShowroomCameraState.ArcRotate) {
            const targetRadius = value * (this._arcRotateCamera.upperRadiusLimit - this._arcRotateCamera.lowerRadiusLimit) + this._arcRotateCamera.lowerRadiusLimit;
            const arcRotateCamera = this._arcRotateCamera;
            this._perFrameObservable.cancelAllCoroutines();
            this._perFrameObservable.runCoroutineAsync(function* () {
                while (Math.abs(targetRadius - arcRotateCamera.radius) > 0.001) {
                    arcRotateCamera.radius = 0.05 * targetRadius + 0.95 * arcRotateCamera.radius;
                    yield;
                }
            }());
        }
    }
    _matchmove(state) {
        var _a;
        this._transform.position.copyFrom(state.matchmoveTarget.absolutePosition);
        this._transform.rotationQuaternion.copyFrom(state.matchmoveTarget.absoluteRotationQuaternion);
        this._currentFocusPosition.copyFrom(state.matchmoveTarget.absolutePosition);
        state.matchmoveTarget.forward.scaleAndAddToRef((_a = state.focusDepth) !== null && _a !== void 0 ? _a : 1, this._currentFocusPosition);
    }
    *_matchmoveCoroutine(state) {
        while (true) {
            this._matchmove(state);
            yield;
        }
    }
    _poseArcRotateCamera(state) {
        var _a, _b, _c;
        this._arcRotateCamera.target = state.startingPosition;
        this._arcRotateCamera.lowerRadiusLimit = 0;
        this._arcRotateCamera.upperRadiusLimit = 0;
        this._arcRotateCamera.radius = 0;
        this._arcRotateCamera.position.copyFrom(state.startingPosition);
        this._arcRotateCamera.upperRadiusLimit = (_a = state.upperRadiusLimit) !== null && _a !== void 0 ? _a : 2 * core_1.Vector3.Distance(state.startingPosition, state.target);
        this._arcRotateCamera.lowerRadiusLimit = (_b = state.lowerRadiusLimit) !== null && _b !== void 0 ? _b : 0.1 * this._arcRotateCamera.upperRadiusLimit;
        this._arcRotateCamera.wheelDeltaPercentage = (_c = state.wheelDeltaPercentage) !== null && _c !== void 0 ? _c : 0.01;
        this._arcRotateCamera.setTarget(state.target);
    }
    _getArcRotateCameraPoseComponentsToRef(position, forward, up, focusPoint) {
        // Though tricky and hard to read, the simplest way to directly align the transform with
        // the ArcRotateCamera is to use the world matrix.
        const mat = this._arcRotateCamera.getWorldMatrix();
        position.copyFromFloats(mat.m[12], mat.m[13], mat.m[14]);
        forward.copyFromFloats(mat.m[8], mat.m[9], mat.m[10]);
        up.copyFromFloats(mat.m[4], mat.m[5], mat.m[6]);
        focusPoint.copyFrom(this._arcRotateCamera.target);
    }
    _alignTransformToArcRotateCamera() {
        const forward = core_1.TmpVectors.Vector3[0];
        const up = core_1.TmpVectors.Vector3[1];
        const focusPoint = core_1.TmpVectors.Vector3[2];
        this._getArcRotateCameraPoseComponentsToRef(this._transform.position, forward, up, focusPoint);
        core_1.Quaternion.FromLookDirectionRHToRef(forward, up, this._transform.rotationQuaternion);
    }
    setToMatchmoveState(state) {
        this._arcRotateCamera.detachControl();
        this._scene.setActiveCameraByName("showroomCamera");
        this._matchmove(state);
        this._perFrameObservable.cancelAllCoroutines();
        this._perFrameObservable.runCoroutineAsync(this._matchmoveCoroutine(state));
        this._currentState = ShowroomCameraState.Matchmove;
    }
    setToArcRotateState(state) {
        this._perFrameObservable.cancelAllCoroutines();
        this._scene.setActiveCameraByName("showroomArcRotateCamera");
        this._poseArcRotateCamera(state);
        this._arcRotateCamera.attachControl();
        if (!this._enableMouseWheel) {
            this._arcRotateCamera.inputs.remove(this._arcRotateCamera.inputs.attached.mousewheel);
        }
        this._currentFocusPosition.copyFrom(state.target);
        this._currentState = ShowroomCameraState.ArcRotate;
    }
    *_animateToMatchmoveStateCoroutine(state, seconds) {
        var _a;
        if (this._currentState === ShowroomCameraState.ArcRotate) {
            this._alignTransformToArcRotateCamera();
            this._scene.setActiveCameraByName("showroomCamera");
            this._arcRotateCamera.detachControl();
            this._currentState = ShowroomCameraState.Matchmove;
        }
        const startingPosition = this._transform.position.clone();
        const startingFocus = this._currentFocusPosition.clone();
        const startingUp = this._transform.up.clone();
        const ANIMATION_FRAMES = Math.round(seconds * 60 / Math.max(this._scene.getAnimationRatio(), 1));
        for (let frame = 0; frame <= ANIMATION_FRAMES; ++frame) {
            let t = frame / ANIMATION_FRAMES;
            core_1.Vector3.LerpToRef(startingPosition, state.matchmoveTarget.absolutePosition, t, this._transform.position);
            const targetFocus = core_1.TmpVectors.Vector3[0];
            targetFocus.copyFrom(state.matchmoveTarget.absolutePosition);
            state.matchmoveTarget.forward.scaleAndAddToRef((_a = state.focusDepth) !== null && _a !== void 0 ? _a : 1, targetFocus);
            const focusT = core_1.TmpVectors.Vector3[1];
            core_1.Vector3.LerpToRef(startingFocus, targetFocus, t, focusT);
            this._currentFocusPosition.copyFrom(focusT);
            // We reuse temp vector 0 for this because we no longer need targetFocus.
            const forwardT = core_1.TmpVectors.Vector3[0];
            focusT.subtractToRef(this._transform.position, forwardT);
            forwardT.normalize();
            // Likewise, we reuse temp vector 1 because focusT is not needed after calculating forwardT.
            const upT = core_1.TmpVectors.Vector3[1];
            core_1.Vector3.LerpToRef(startingUp, state.matchmoveTarget.up, t, upT);
            const rightT = core_1.TmpVectors.Vector3[2];
            core_1.Vector3.CrossToRef(forwardT, upT, rightT);
            core_1.Vector3.CrossToRef(rightT, forwardT, upT);
            upT.normalize();
            core_1.Quaternion.FromLookDirectionRHToRef(forwardT, upT, this._transform.rotationQuaternion);
            yield;
        }
        this.setToMatchmoveState(state);
    }
    animateToMatchmoveState(state, seconds = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            this._perFrameObservable.cancelAllCoroutines();
            return this._perFrameObservable.runCoroutineAsync(this._animateToMatchmoveStateCoroutine(state, seconds));
        });
    }
    *_animateToArcRotateStateCoroutine(state, seconds) {
        if (this._currentState === ShowroomCameraState.ArcRotate) {
            this._alignTransformToArcRotateCamera();
            this._scene.setActiveCameraByName("showroomCamera");
            this._arcRotateCamera.detachControl();
            this._currentState = ShowroomCameraState.Matchmove;
        }
        this._transform.computeWorldMatrix(true);
        const startingPosition = this._transform.position.clone();
        const startingUp = this._transform.up.clone();
        const startingFocus = this._currentFocusPosition.clone();
        this._poseArcRotateCamera(state);
        const destinationPosition = new core_1.Vector3();
        const destinationUp = new core_1.Vector3();
        const destinationFocus = new core_1.Vector3();
        this._getArcRotateCameraPoseComponentsToRef(destinationPosition, core_1.TmpVectors.Vector3[0], destinationUp, destinationFocus);
        const ANIMATION_FRAMES = Math.round(seconds * 60 / Math.max(this._scene.getAnimationRatio(), 1));
        for (let frame = 0; frame <= ANIMATION_FRAMES; ++frame) {
            let t = frame / ANIMATION_FRAMES;
            core_1.Vector3.LerpToRef(startingPosition, destinationPosition, t, this._transform.position);
            const focusT = core_1.TmpVectors.Vector3[1];
            core_1.Vector3.LerpToRef(startingFocus, destinationFocus, t, focusT);
            this._currentFocusPosition.copyFrom(focusT);
            const forwardT = core_1.TmpVectors.Vector3[0];
            focusT.subtractToRef(this._transform.position, forwardT);
            forwardT.normalize();
            // Likewise, we reuse temp vector 1 because focusT is not needed after calculating forwardT.
            const upT = core_1.TmpVectors.Vector3[1];
            core_1.Vector3.LerpToRef(startingUp, destinationUp, t, upT);
            const rightT = core_1.TmpVectors.Vector3[2];
            core_1.Vector3.CrossToRef(forwardT, upT, rightT);
            core_1.Vector3.CrossToRef(rightT, forwardT, upT);
            upT.normalize();
            core_1.Quaternion.FromLookDirectionRHToRef(forwardT, upT, this._transform.rotationQuaternion);
            yield;
        }
        this.setToArcRotateState(state);
    }
    animateToArcRotateState(state, seconds = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            this._perFrameObservable.cancelAllCoroutines();
            return this._perFrameObservable.runCoroutineAsync(this._animateToArcRotateStateCoroutine(state, seconds));
        });
    }
    static Demo(canvas) {
        const engine = new core_1.Engine(canvas);
        const scene = new core_1.Scene(engine);
        const sphere = core_1.MeshBuilder.CreateSphere("sphere", { diameter: 2 }, scene);
        sphere.position.y = 1;
        const cube = core_1.MeshBuilder.CreateBox("cube", { size: 0.5 }, scene);
        cube.position.y = 0.25;
        cube.position.z = -2;
        core_1.MeshBuilder.CreateGround("ground", { width: 6, height: 6 }, scene);
        scene.createDefaultLight();
        const camera = new ShowroomCamera(scene);
        const pylon = new core_1.TransformNode("pylon", scene);
        pylon.rotationQuaternion = core_1.Quaternion.Identity();
        const matchmove = new core_1.TransformNode("matchmove", scene);
        matchmove.rotationQuaternion = core_1.Quaternion.Identity();
        matchmove.parent = pylon;
        matchmove.position.z = -10;
        pylon.rotate(core_1.Vector3.RightReadOnly, 0.1);
        scene.onBeforeRenderObservable.runCoroutineAsync(function* () {
            while (true) {
                pylon.rotate(core_1.Vector3.UpReadOnly, 0.01, core_1.Space.WORLD);
                yield;
            }
        }());
        const matchmoveState = {
            matchmoveTarget: matchmove,
            focusDepth: 10
        };
        const arcRotateState = {
            startingPosition: new core_1.Vector3(0, 5, -10),
            target: new core_1.Vector3(0, 1, 0)
        };
        const setStateFunction = function () {
            return __awaiter(this, void 0, void 0, function* () {
                while (true) {
                    camera.setToArcRotateState(arcRotateState);
                    yield core_1.Tools.DelayAsync(7000);
                    camera.setToMatchmoveState(matchmoveState);
                    yield core_1.Tools.DelayAsync(7000);
                    yield camera.animateToArcRotateState(arcRotateState);
                    yield core_1.Tools.DelayAsync(7000);
                    yield camera.animateToMatchmoveState(matchmoveState);
                    yield core_1.Tools.DelayAsync(7000);
                    camera.animateToArcRotateState(arcRotateState, 4);
                    yield core_1.Tools.DelayAsync(3000);
                    camera.animateToMatchmoveState(matchmoveState, 4);
                    yield core_1.Tools.DelayAsync(2000);
                }
            });
        };
        setStateFunction();
        engine.runRenderLoop(() => {
            scene.render();
        });
    }
}
exports.ShowroomCamera = ShowroomCamera;
